import { useEffect, useState, useContext } from 'react';
import Layout from '@/components/Layout';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Edit3, ArrowLeft, Trash2 } from 'lucide-react';
import { AuthContext } from '@/App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIER_LABELS = {
  'basic': 'BASIC PRACTICE',
  'comps_ready': "COMP'S READY",
  'advanced': 'ADVANCED'
};

export default function FillInCategoryPage() {
  const { tier, categorySlug } = useParams();
  const { user } = useContext(AuthContext);
  const isAdmin = user?.role === 'admin';
  const [fillIns, setFillIns] = useState([]);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, [tier, categorySlug]);

  const loadData = async () => {
    try {
      // Load all fill-ins and filter by tier and category
      const [fillInsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/fill-ins`),
        axios.get(`${API}/test-categories`) // Public endpoint
      ]);
      
      // Find the category
      const foundCategory = categoriesRes.data.find(cat => cat.slug === categorySlug);
      setCategory(foundCategory);
      
      // Filter fill-ins for this tier and category
      const filtered = fillInsRes.data.filter(
        f => f.tier === tier && f.category === categorySlug
      );
      setFillIns(filtered);
    } catch (error) {
      console.error('Failed to load fill-ins:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFillInClick = (fillInId) => {
    navigate(`/fill-in/${fillInId}`);
  };

  const handleBackClick = () => {
    navigate('/fill-in-center');
  };

  const handleDelete = async (fillInId, title) => {
    if (!window.confirm(`Delete Fill In exercise "${title || 'this exercise'}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/fill-ins/${fillInId}`, { withCredentials: true });
      setFillIns(prev => prev.filter(f => f.id !== fillInId));
      toast.success('Fill In exercise deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete exercise');
    }
  };

  return (
    <Layout>
      {/* Hero Section */}
      <div 
        className="relative bg-gradient-to-br from-blue-400 via-blue-500 to-blue-600 h-[300px] mb-12 flex items-center justify-center"
      >
        <div className="container mx-auto px-6">
          <button
            onClick={handleBackClick}
            className="flex items-center gap-2 text-white hover:text-[#F4E4C1] transition-colors mb-6"
          >
            <ArrowLeft className="w-6 h-6" />
            <span className="text-lg font-bold">Back to Fill In Center</span>
          </button>
          <div className="text-center">
            <div className="text-6xl mb-4">{category?.icon || '✏️'}</div>
            <h1 className="text-5xl md:text-6xl retro-title text-white mb-2">
              {category?.name.toUpperCase() || categorySlug.toUpperCase()}
            </h1>
            <p className="text-xl text-white font-bold mb-2">
              {TIER_LABELS[tier] || tier.toUpperCase()}
            </p>
            <p className="text-lg text-white opacity-90">
              {fillIns.length} Fill In Exercise{fillIns.length !== 1 ? 's' : ''} Available
            </p>
          </div>
        </div>
      </div>

      {/* Fill-Ins Grid */}
      <div className="container mx-auto px-6 py-12">
        {loading ? (
          <div className="text-center py-20">
            <div className="text-2xl text-[#6B4423]">Loading exercises...</div>
          </div>
        ) : fillIns.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {fillIns.map((fillIn) => (
              <div key={fillIn.id} className="relative" data-testid={`fill-in-card-${fillIn.id}`}>
                {isAdmin && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDelete(fillIn.id, fillIn.test_title); }}
                    className="absolute top-3 right-3 z-10 bg-[#E85D4D] text-white p-2 rounded-full hover:bg-[#d14a3b] transition-colors shadow-lg"
                    title="Delete this Fill In exercise"
                    data-testid={`delete-fill-in-button-${fillIn.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={() => handleFillInClick(fillIn.id)}
                  className="w-full bg-gradient-to-br from-blue-100 to-blue-200 border-4 border-[#4A90E2] p-8 rounded-lg text-left transition-all transform hover:scale-105 hover:shadow-2xl"
                >
                  <div className="flex items-start justify-between mb-4">
                    <Edit3 className="w-10 h-10 text-[#357ABD]" />
                    <span className="bg-[#4A90E2] text-white px-3 py-1 rounded-full text-sm font-bold">
                      {fillIn.sets && fillIn.sets.length > 0 ? fillIn.sets.flat().length : (fillIn.questions?.length || 0)} Questions
                      {fillIn.sets && fillIn.sets.length > 1 ? ` (${fillIn.sets.length} sets)` : ''}
                    </span>
                  </div>
                  <h3 className="text-2xl font-bold text-[#6B4423] mb-3 retro-title">
                    {fillIn.test_title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Fill in the blanks. AI-graded by Ernie.AI. Printable for practice.
                  </p>
                  <div className="text-xs text-gray-500">
                    Created {new Date(fillIn.created_at).toLocaleDateString()}
                  </div>
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <Edit3 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <div className="text-2xl text-gray-600 mb-2">No Fill In exercises in this category yet</div>
            <p className="text-gray-500">Check back later or try another category!</p>
          </div>
        )}
      </div>
    </Layout>
  );
}
